// Angular imports
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';

// Third party imports
import { Subscription } from 'rxjs';

// devextreme imports
import notify from 'devextreme/ui/notify';

// CC-Common Imports
import { IconConstants, MessageBoxComponent, Utils } from 'cc-common';

// Constant imports
import { StringFormatConstants } from '../../../../_constants/string-format-constants';
import { MessageInterval } from '../../../../_constants/messages-intervals';

// Service imports
import { ServerErrorService } from '../../../../_services/shared-services/server-error.service';
import { ARCreditApplicationService } from '../../_services/account-receivable/ar-credit-application.service';
import { PrecisionService } from '../../../../_services/shared-services/precision.service';
import { DetailPopupsService } from '../../_services/detail-popups.service';
import { UserService } from '../../../../_services/data-services/user.service';

// Model imports
import { ARPaymentParameterDto } from '../../_models/account-receivable/ar-payment/ar-payment-parameter-dto';
import { ARAdjustmentParameterDto } from '../../_models/account-receivable/ar-adjustment/ar-adjustment-parameter-dto';
import { ARInvoiceDetailParameterDto } from '../../_models/account-receivable/ar-invoice/ar-invoice-detail-parameter-dto';
import { ARCreditApplicationParameterDto } from '../../_models/account-receivable/credit-applications/ar-credit-application-parameter-dto';
import { ARCreditApplicationsResultDto } from '../../_models/account-receivable/credit-applications/ar-credit-applications-result-dto';

@Component({
    selector: 'app-ar-credit-application',
    templateUrl: './ar-credit-application.component.html',
    styleUrls: ['./ar-credit-application.component.scss']
})
export class ARCreditApplicationComponent implements OnInit, OnDestroy {
    @ViewChild('creditApplicationMessageBox', { static: false }) creditApplicationMessageBox: MessageBoxComponent;

    @Input() visible = false;
    @Input() creditApplicationData: ARCreditApplicationsResultDto[] = [];
    @Input() transactionType: string;
    @Input() transactionId: number;
    @Input() communityId: number;
    @Input() managementCompanyId: number;
    @Output() onCreditApplicationClose = new EventEmitter<boolean>();

    loadingVisible = false;
    popUpTitle: string;
    currencyFormat: any;
    arPaymentParams = new ARPaymentParameterDto();
    arAdjustmentDetailParam = new ARAdjustmentParameterDto();
    arInvoiceDetailParam = new ARInvoiceDetailParameterDto();

    arPaymentDetailVisible = false;
    arAdjustmentDetailVisible = false;
    arInvoiceDetailVisible = false;

    dateFormat = StringFormatConstants.dateFormat;

    displayPaymentDetails = false;
    isInternalUser: boolean;

    forceRefresh = false;

    iconClose = IconConstants.CROSS_MARK;
    forceReloadSubscription: Subscription;

    constructor(
        private readonly arCreditApplicationService: ARCreditApplicationService,
        private readonly errorService: ServerErrorService,
        private readonly precisionService: PrecisionService,
        private readonly detailPopupsService: DetailPopupsService,
        private readonly userService: UserService
    ) {
        this.currencyFormat = this.precisionService.getCurrencyFormat();
    }

    ngOnInit() {
        this.isInternalUser = this.userService.isInternal();
        if (!!this.transactionType && !!this.transactionId) {
            this.popUpTitle = `Credit Application AR ${this.transactionType} ${this.transactionId}`;
        }

        this.forceReloadSubscription = this.detailPopupsService.onForceReload().subscribe(() => {
            this.forceRefresh = true;
        });
    }

    ngOnDestroy() {
        if (Utils.notNullAndDefined(this.forceReloadSubscription)) {
            this.forceReloadSubscription.unsubscribe();
        }
    }

    onHidden(): void {
        this.closeCreditApplication();
    }

    onPaymentClick(data: ARCreditApplicationsResultDto): void {
        if (Utils.equalsIgnoreCase(data.source.type, 'P')) {
            this.arPaymentParams.managementCompanyId = this.managementCompanyId;
            this.arPaymentParams.communityId = this.communityId;
            this.arPaymentParams.paymentId = data.source.id;
            this.arPaymentParams.clearCache = false;
            this.arPaymentDetailVisible = true;
        } else if (Utils.equalsIgnoreCase(data.source.type, 'A')) {
            this.arAdjustmentDetailParam.managementCompanyId = this.managementCompanyId;
            this.arAdjustmentDetailParam.communityId = this.communityId;
            this.arAdjustmentDetailParam.adjustmentId = data.source.id;
            this.arAdjustmentDetailParam.clearCache = false;
            this.arAdjustmentDetailVisible = true;
        } else if (Utils.equalsIgnoreCase(data.source.type, 'I')) {
            this.arInvoiceDetailParam.managementCompanyId = this.managementCompanyId;
            this.arInvoiceDetailParam.communityId = this.communityId;
            this.arInvoiceDetailParam.invoiceId = data.source.id;
            this.arInvoiceDetailParam.clearCache = false;
            this.arInvoiceDetailVisible = true;
        } else {
            this.detailPopupsService.showDetailsPopup('AR', data.source.type, {
                managementCompanyId: this.managementCompanyId,
                communityId: this.communityId,
                headerId: data.source.id
            });
        }
    }

    onARPaymentDetailClose(e: any): void {
        this.arPaymentDetailVisible = false;
        this.arPaymentParams = new ARPaymentParameterDto();
        if (Utils.isTrue(e)) {
            this.detailPopupsService.updateForceReload(e);
        }
    }

    onARAdjustmentDetailClose(e: any): void {
        this.arAdjustmentDetailVisible = false;
        this.arAdjustmentDetailParam = new ARAdjustmentParameterDto();
        if (Utils.isTrue(e)) {
            this.detailPopupsService.updateForceReload(e);
        }
    }

    onARInvoiceDetailClose(e: any): void {
        this.arInvoiceDetailVisible = false;
        this.arInvoiceDetailParam = new ARInvoiceDetailParameterDto();
        if (Utils.isTrue(e)) {
            this.detailPopupsService.updateForceReload(e);
        }
    }

    onARInvoiceActivitySuccess(): void {
        this.detailPopupsService.updateForceReload(true);
    }

    onTargetClick(data: ARCreditApplicationsResultDto): void {
        if (Utils.equalsIgnoreCase(data.target.type, 'P')) {
            this.arPaymentParams.managementCompanyId = this.managementCompanyId;
            this.arPaymentParams.communityId = this.communityId;
            this.arPaymentParams.paymentId = data.target.id;
            this.arPaymentParams.clearCache = false;
            this.arPaymentDetailVisible = true;
        } else if (Utils.equalsIgnoreCase(data.target.type, 'A')) {
            this.arAdjustmentDetailParam.managementCompanyId = this.managementCompanyId;
            this.arAdjustmentDetailParam.communityId = this.communityId;
            this.arAdjustmentDetailParam.adjustmentId = data.target.id;
            this.arAdjustmentDetailParam.clearCache = false;
            this.arAdjustmentDetailVisible = true;
        } else if (Utils.equalsIgnoreCase(data.target.type, 'I')) {
            this.arInvoiceDetailParam.managementCompanyId = this.managementCompanyId;
            this.arInvoiceDetailParam.communityId = this.communityId;
            this.arInvoiceDetailParam.invoiceId = data.target.id;
            this.arInvoiceDetailParam.clearCache = false;
            this.arInvoiceDetailVisible = true;
        } else {
            this.detailPopupsService.showDetailsPopup('AR', data.target.type, {
                managementCompanyId: this.managementCompanyId,
                communityId: this.communityId,
                headerId: data.target.id
            });
        }
    }

    onUnapplyClick(data: ARCreditApplicationsResultDto): void {
        // DEV-3073, we decided that we always display the unapply menu. If it is not allow to unapply, we display a message when the user clicks on it instead of hiding the menu.
        if (Utils.isFalse(data.allowUnapply)) {
            this.creditApplicationMessageBox.showErrorMessage(
                'Error',
                'System Generated Transaction, Unapply not allowed for this transaction.'
            );
        } else {
            const param: ARCreditApplicationParameterDto = new ARCreditApplicationParameterDto();
            param.communityId = this.communityId;
            param.managementCompanyId = this.managementCompanyId;
            param.creditApplicationId = data.creditApplicationId;
            this.loadingVisible = true;
            this.arCreditApplicationService.unapplyCreditApplication(param).subscribe(
                () => {
                    notify('Credit application unapplied successfully', 'success', MessageInterval.successMsgInterval);
                    this.forceRefresh = true;
                    this.closeCreditApplication();
                    this.loadingVisible = false;
                },
                (error) => {
                    this.loadingVisible = false;
                    this.errorService.displayError(error);
                    this.onHidden();
                }
            );
        }
    }

    private closeCreditApplication(): void {
        this.visible = false;
        this.onCreditApplicationClose.emit(this.forceRefresh);
    }
}
